// WHAT: Log filtering component for debug overlay with hierarchical scope filtering
// WHY:  Provides comprehensive filtering interface for log entries by domain, level, module, file, function, and search term
// DOMAIN: Logging → Debug Overlay → Filter Management
// DEPENDENCIES: useDebugSystem hook, useLogStore hook, LOG_DOMAINS, LOG_LEVELS entities
// RISKS:
//   - NOTE: Hierarchical filter state management is complex - may cause performance issues with many logs
//   - NOTE: Auto-scroll toggle affects global state - may interfere with user's manual scrolling
//   - WARN: Filter clearing may not reset all nested filter states correctly
//   - NOTE: Search term filtering is case-sensitive - may miss matches

import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { useState } from "react";

import { LOG_LEVELS, getLogDomainConfig, getLogLevelConfig, type LogDomain } from "../../entities";
import { useDebugSystem } from "../../hooks/useDebugSystem";
import { useLogStore } from "../../hooks/useLogStore";

/**
 * WHAT:
 *   Log filtering component for debug overlay with hierarchical scope filtering.
 *
 * WHY:
 *   Provides comprehensive filtering interface for log entries by domain, level, module, file, function, and search term.
 *
 * PROPS:
 *   None (uses hooks for state management)
 *
 * BEHAVIOR:
 *   - Integrates with useDebugSystem hook for filter state
 *   - Integrates with useLogStore hook for error-only filtering
 *   - Provides domain toggle buttons with hierarchical structure
 *   - Supports level filtering (debug, info, warn, error)
 *   - Provides search term input for message filtering
 *   - Supports hierarchical scope filtering (module, file, function)
 *   - Toggles auto-scroll functionality
 *   - Provides clear filters functionality
 *
 * RISKS:
 *   - NOTE: Hierarchical filter state management is complex - may cause performance issues with many logs
 *   - NOTE: Auto-scroll toggle affects global state - may interfere with user's manual scrolling
 *   - WARN: Filter clearing may not reset all nested filter states correctly
 *   - NOTE: Search term filtering is case-sensitive - may miss matches
 *
 * @returns React component for log filters
 *
 */
export const LogFilters: React.FC = () => {
  const {
    autoScroll,
    availableDomains,
    availableFiles,
    availableFunctions,
    availableModules,
    clearFilters,
    refreshLogs,
    searchTerm,
    selectedDomains,
    selectedFile,
    selectedFunction,
    selectedLevel,
    selectedModule,
    selectFile,
    selectFunction,
    selectModule,
    setLevel,
    setSearchTerm,
    toggleAutoScroll,
    toggleDomain,
  } = useDebugSystem();

  const { setShowErrorsOnly, showErrorsOnly } = useLogStore();

  // Single toggle for entire hierarchical structure
  const [showHierarchy, setShowHierarchy] = useState(false);

  // Extract handlers to reduce nesting depth
  const handleFileClick = (file: string) => {
    selectFile(file);
  };

  const handleFunctionClick = (functionName: string) => {
    selectFunction(functionName);
  };

  // Extract nested components to reduce nesting depth
  const FunctionList: React.FC<{
    functions: string[];
    isFileSelected: boolean;
    selectedFunction?: string;
  }> = ({ functions, isFileSelected, selectedFunction: selectedFunc }) => {
    if (!isFileSelected || functions.length === 0) {
      return null;
    }
    return (
      <div className="ml-4 space-y-1 border-l border-gray-600 pl-2">
        {functions.map((function_) => {
          const isFunctionSelected = selectedFunc === function_;
          return (
            <div className="flex items-center space-x-1" key={function_}>
              <button
                className={`
                  flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                  transition-colors
                  ${
                    isFunctionSelected
                      ? "bg-purple-500 text-white"
                      : "bg-gray-400 text-gray-300 hover:bg-gray-300"
                  }
                `}
                onClick={() => {
                  handleFunctionClick(function_);
                }}
              >
                <span>⚙️</span>
                <span>{function_}</span>
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const FileList: React.FC<{
    availableFunctions: string[];
    files: string[];
    isModuleSelected: boolean;
    selectedFile?: string;
    selectedFunction?: string;
  }> = ({
    availableFunctions: availableFuncs,
    files,
    isModuleSelected,
    selectedFile: selectedFileProp,
    selectedFunction: selectedFunc,
  }) => {
    if (!isModuleSelected || files.length === 0) {
      return null;
    }
    return (
      <div className="ml-4 space-y-1 border-l border-gray-600 pl-2">
        {files.map((file) => {
          const isFileSelected = selectedFileProp === file;
          return (
            <div className="space-y-1" key={file}>
              <div className="flex items-center space-x-1">
                <button
                  className={`
                    flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                    transition-colors
                    ${
                      isFileSelected
                        ? "bg-yellow-500 text-white"
                        : "bg-gray-500 text-gray-300 hover:bg-gray-400"
                    }
                  `}
                  onClick={() => {
                    handleFileClick(file);
                  }}
                >
                  <span>📄</span>
                  <span>{file}</span>
                </button>
              </div>
              <FunctionList
                functions={availableFuncs}
                isFileSelected={isFileSelected}
                {...(selectedFunc === undefined ? {} : { selectedFunction: selectedFunc })}
              />
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="p-3 bg-gray-800">
      {/* Search Bar */}
      <div className="relative mb-3">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          className="w-full pl-10 pr-10 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
          placeholder="Nachrichten durchsuchen..."
          type="text"
          value={searchTerm}
        />
        {searchTerm ? (
          <button
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
            onClick={() => {
              setSearchTerm("");
            }}
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {/* Level Filter */}
      <div className="mb-3">
        <label className="block text-xs font-medium text-gray-300 mb-1">Log Level</label>
        <div className="flex space-x-1">
          {LOG_LEVELS.map((level) => {
            const config = getLogLevelConfig(level);
            return (
              <button
                className={`
                  flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                  transition-colors
                  ${
                    selectedLevel === level
                      ? "bg-blue-500 text-white"
                      : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }
                `}
                key={level}
                onClick={() => {
                  setLevel(level);
                }}
              >
                <span>{config.icon}</span>
                <span>{config.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Error-Only Filter */}
      <div className="mb-3">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            checked={showErrorsOnly}
            className="w-4 h-4 text-red-500 bg-gray-700 border-gray-600 rounded focus:ring-red-500 focus:ring-2"
            onChange={(e) => {
              setShowErrorsOnly(e.target.checked);
            }}
            type="checkbox"
          />
          <span className="text-xs font-medium text-gray-300">🔴 Nur Errors anzeigen</span>
        </label>
      </div>

      {/* Hierarchical Filter Toggle */}
      <div className="mb-3">
        <button
          className="flex items-center space-x-2 text-xs text-gray-400 hover:text-white transition-colors w-full"
          onClick={() => {
            setShowHierarchy(!showHierarchy);
          }}
        >
          {showHierarchy ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronRightIcon className="h-3 w-3" />
          )}
          <span>Filter nach Scope</span>
        </button>

        {/* Hierarchical Content */}
        {showHierarchy && availableDomains.length > 0 ? (
          <div className="ml-4 space-y-1 border-l border-gray-600 pl-2">
            {availableDomains.map((domain) => {
              let domainKey: string;
              let domainLabel: string;

              if (typeof domain === "string") {
                domainKey = domain;
                domainLabel = domain;
              } else {
                const domainObj = domain as {
                  domain?: string;
                  label?: string;
                  name?: string;
                };
                domainKey = domainObj.name ?? domainObj.domain ?? JSON.stringify(domainObj);
                domainLabel = domainObj.label ?? domainObj.name ?? domainObj.domain ?? "unknown";
              }

              const config = getLogDomainConfig(domainLabel as LogDomain);
              const isSelected = selectedDomains.includes(domainLabel as LogDomain);

              return (
                <div className="space-y-1" key={domainKey}>
                  {/* Domain Row */}
                  <div className="flex items-center space-x-1">
                    <button
                      className={`
                        flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                        transition-colors
                        ${
                          isSelected
                            ? "bg-blue-500 text-white"
                            : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        }
                      `}
                      onClick={() => {
                        toggleDomain(domainLabel as LogDomain);
                      }}
                    >
                      <span>{config.icon}</span>
                      <span>{config.label}</span>
                    </button>
                  </div>

                  {/* Modules for this domain */}
                  {isSelected && availableModules.length > 0 ? (
                    <div className="ml-4 space-y-1 border-l border-gray-600 pl-2">
                      {availableModules.map((module) => {
                        const isModuleSelected = selectedModule === module;

                        return (
                          <div className="space-y-1" key={module}>
                            {/* Module Row */}
                            <div className="flex items-center space-x-1">
                              <button
                                className={`
                                  flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium
                                  transition-colors
                                  ${
                                    isModuleSelected
                                      ? "bg-green-500 text-white"
                                      : "bg-gray-600 text-gray-300 hover:bg-gray-500"
                                  }
                                `}
                                onClick={() => {
                                  selectModule(module);
                                }}
                              >
                                <span>📦</span>
                                <span>{module}</span>
                              </button>
                            </div>

                            {/* Files for this module */}
                            <FileList
                              availableFunctions={availableFunctions}
                              files={availableFiles}
                              isModuleSelected={isModuleSelected}
                              {...(selectedFile === undefined ? {} : { selectedFile })}
                              {...(selectedFunction === undefined ? {} : { selectedFunction })}
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : null}

        {showHierarchy && availableDomains.length === 0 ? (
          <div className="ml-4 text-xs text-gray-500 italic">
            Keine Domains in den Logs gefunden
          </div>
        ) : null}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-700">
        <div className="flex items-center space-x-2">
          <button
            className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
            onClick={clearFilters}
          >
            <XMarkIcon className="h-3 w-3" />
            <span>Filter zurücksetzen</span>
          </button>

          <button
            className={`flex items-center space-x-1 px-2 py-1 text-xs transition-colors ${
              autoScroll ? "bg-green-600 text-white" : "text-gray-400 hover:text-white"
            }`}
            onClick={toggleAutoScroll}
            title={autoScroll ? "Auto-Scroll deaktivieren" : "Auto-Scroll aktivieren"}
          >
            <span>{autoScroll ? "⏸️" : "▶️"}</span>
            <span>Auto-Scroll</span>
          </button>
        </div>

        <button
          className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-400 hover:text-white transition-colors"
          onClick={() => {
            void refreshLogs();
          }}
        >
          <ArrowPathIcon className="h-3 w-3" />
          <span>Aktualisieren</span>
        </button>
      </div>
    </div>
  );
};
